import { useDropzone } from "react-dropzone";
import { cn } from "../../../../utils";
import { ReactNode } from "react";
import { Tooltip } from "antd";
import { CircleX } from "lucide-react";

interface DragAndDropUploadProps {
  children: ReactNode;
  className?: string;
  onDrop: (acceptedFiles: File[]) => void;
}

interface DragAndDropTextProps {
  children: ReactNode;
  className?: string;
}

export const DragAndDropUpload = ({
  children,
  className,
  onDrop,
  accept = {
    "image/png": [".png"],
    "image/jpeg": [".jpg", ".jpeg"],
    "application/pdf": [".pdf"],
  },
}: DragAndDropUploadProps) => {
  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    accept,
    maxSize: 5 * 1024 * 1024, // 5MB limit
  });
  const fileRejectionItems = fileRejections.map(({ errors }) => (
    <div className="text-base text-white">
      {errors.map((e) => (
        <li key={e.code}>{e.message}</li>
      ))}
    </div>
  ));

  return (
    <div
      {...getRootProps()}
      className={cn(
        "border relative flex-col border-[#E5E7EB] rounded-sm text-center cursor-pointer transition-all flex justify-center items-center shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25),_0px_10px_15px_0px_rgba(0,0,0,0.10)]",
        className
      )}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center justify-center w-full h-full">
        {children}
      </div>
      {!!fileRejections.length && (
        <Tooltip placement="top" title={fileRejectionItems}>
          <div className="flex items-center text-red-700 mt-2 absolute bottom-0 left-0 w-full flex justify-center">
            <CircleX className="h-4 w-4 mr-2" />
            Error. Hover for details.
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export const DragAndDropTitle = ({
  children,
  className,
}: DragAndDropTextProps) => (
  <div className={cn("text-sm font-bold", className)}>{children}</div>
);

export const DragAndDropHelpText = ({
  children,
  className,
}: DragAndDropTextProps) => (
  <div
    className={cn("text-sm font-medium", className)}
    style={{ color: "#6B7280" }}
  >
    {children}
  </div>
);

export const DragAndDropMaxSizeText = ({
  children,
  className,
}: DragAndDropTextProps) => (
  <div
    className={cn("text-xs font-medium", className)}
    style={{ color: "#9CA3AF" }}
  >
    {children}
  </div>
);
